import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { App, Button, Card, Checkbox, Col, DatePicker, Divider, Form, Input, Radio, Row, Select, Space, Tabs } from 'antd';
import { listGenders, listMainGoals, listFitnessLevels } from '../../services/catalogs';
import { lookupPostalCode, lookupPostalCodeById } from '../../services/geo';
import { getClientById, createClient, updateClient, uploadClientPhoto, checkClientNicknameAvailability } from '../../services/clients';
import dayjs from 'dayjs';
import MediaUpload from '../../components/MediaUpload/MediaUpload';
import ClientSubscriptionsHistory from './ClientSubscriptionsHistory';

const { useApp } = App;


const ddmmyyyy = /^\d{2}\/\d{2}\/\d{4}$/;
const phone10 = /^\d{10}$/;

export default function ClientUpsertPage() {
    const { id } = useParams();
    const isEdit = Boolean(id);
    const navigate = useNavigate();
    const { message } = useApp();
    const [form] = Form.useForm();


    const [loading, setLoading] = useState(false);
    const [loadingCP, setLoadingCP] = useState(false);
    const [genders, setGenders] = useState([]);
    const [goals, setGoals] = useState([]);
    const [levels, setLevels] = useState([]);
    const [mediaFile, setMediaFile] = useState(null);
    const [mediaRemoved, setMediaRemoved] = useState(false);
    const [originalMediaPath, setOriginalMediaPath] = useState(null);
    const [nicknameAvailability, setNicknameAvailability] = useState(null);
    const [checkingNickname, setCheckingNickname] = useState(false);
    const [activeTab, setActiveTab] = useState('client');
    const nicknameValidationRequest = useRef(0);

    const handleMediaChange = useCallback((mediaData) => {
        setMediaFile(mediaData.file || null);
        setMediaRemoved(Boolean(mediaData.removed));
    }, []);

    const nicknameValidator = useCallback(async (_, value) => {
        const normalized = value?.trim();
        nicknameValidationRequest.current += 1;
        const requestId = nicknameValidationRequest.current;

        if (!normalized) {
            if (requestId === nicknameValidationRequest.current) {
                setNicknameAvailability(null);
                setCheckingNickname(false);
            }
            return Promise.resolve();
        }

        try {
            setCheckingNickname(true);
            const excludeId = isEdit ? Number(id) : undefined;
            const available = await checkClientNicknameAvailability(normalized, excludeId);

            if (requestId !== nicknameValidationRequest.current) return Promise.resolve();

            setNicknameAvailability(available ? 'available' : 'taken');
            if (!available) {
                return Promise.reject(new Error('Este nickname ya está en uso'));
            }
            return Promise.resolve();
        } catch (err) {
            if (requestId === nicknameValidationRequest.current) {
                setNicknameAvailability('error');
            }
            return Promise.reject(new Error('No se pudo validar el nickname'));
        } finally {
            if (requestId === nicknameValidationRequest.current) {
                setCheckingNickname(false);
            }
        }
    }, [id, isEdit]);

    const nicknameValidateStatus = useMemo(() => {
        if (checkingNickname) return 'validating';
        if (nicknameAvailability === 'available') return 'success';
        if (nicknameAvailability === 'taken' || nicknameAvailability === 'error') return 'error';
        return undefined;
    }, [checkingNickname, nicknameAvailability]);

    // Cargar catálogos
    useEffect(() => {
        (async () => {
            try {
                const [g, m, f] = await Promise.all([
                    listGenders(),
                    listMainGoals(),
                    listFitnessLevels(),
                ]);
                setGenders(g);
                setGoals(m);
                setLevels(f);
            } catch (e) {
                console.error(e);
                message.error('No se pudieron cargar los catálogos');
            }
        })();
    }, [message]);

    // Cargar datos en edición
    useEffect(() => {
        if (!isEdit) return;
        (async () => {
            try {
                setLoading(true);
                const data = await getClientById(id);

                const formValues = {
                    ...data,
                    birth_date: data?.birth_date ? dayjs(data.birth_date) : null,
                    // Asegurar que los IDs sean números para los Select
                    gender_id: data?.gender_id ? Number(data.gender_id) : undefined,
                    main_goal_id: data?.main_goal_id ? Number(data.main_goal_id) : undefined,
                    fitness_level_id: data?.fitness_level_id ? Number(data.fitness_level_id) : undefined,
                    // Normalizar valores booleanos para los consentimientos
                    accept_terms: Boolean(data?.accept_terms),
                    allow_marketing: Boolean(data?.allow_marketing),
                    allow_analytics: Boolean(data?.allow_analytics),
                    // Normalizar has_gym_equipment
                    has_gym_equipment: data?.has_gym_equipment !== undefined ? Boolean(data.has_gym_equipment) : false,
                    // Normalizar otros campos numéricos
                    height_cm: data?.height_cm ? Number(data.height_cm) : undefined,
                    weight_kg: data?.weight_kg ? Number(data.weight_kg) : undefined,
                    weekly_availability_days: data?.weekly_availability_days !== undefined ? Number(data.weekly_availability_days) : undefined,
                };
                form.setFieldsValue(formValues);
                setMediaFile(null);
                setMediaRemoved(false);
                setOriginalMediaPath(data?.photo_url || null);
                // form.setFieldsValue({
                //     ...data,
                //     birth_date: birthStr,
                //     main_goal_id: toNum(data.main_goal_id),
                //     fitness_level_id: toNum(data.fitness_level_id),
                //     weekly_availability_days: toNum(data.weekly_availability_days),
                //     has_gym_equipment: data.has_gym_equipment ? true : false,
                //     accept_terms: data.accept_terms ? true : false,
                //     allow_marketing: data.allow_marketing ? true : false,
                //     allow_analytics: data.allow_analytics ? true : false,
                // });


                // Si hay postal_code_id intenta reconstruir ciudad/estado (si el backend ofrece el endpoint opcional)
                if (data?.postal_code_id) {
                    const geo = await lookupPostalCodeById(data.postal_code_id);
                    if (geo) {
                        form.setFieldsValue({
                            postal_code: geo.postal_code,
                            city_name: geo?.municipality?.name || geo?.city || '',
                            state_name: geo?.state?.name || '',
                            postal_code_id: Number(geo.codigo_postal_id) || data.postal_code_id,
                        });
                    }
                }
            } catch (e) {
                console.error(e);
                message.error('No se pudo cargar el cliente');
            } finally {
                setLoading(false);
            }
        })();
    }, [isEdit, id, form, message]);

    // Handler para cambios en el CP
    const onValuesChange = async (changed) => {
        if (Object.prototype.hasOwnProperty.call(changed, 'nickname')) {
            setNicknameAvailability(null);
        }
        if (Object.prototype.hasOwnProperty.call(changed, 'postal_code')) {
            const raw = String(changed.postal_code || '').replace(/\D/g, '').slice(0, 5);
            form.setFieldsValue({ postal_code: raw });


            if (raw.length === 5) {
                setLoadingCP(true);
                try {
                    const geo = await lookupPostalCode(raw);
                    form.setFieldsValue({
                        city_name: geo?.municipality?.name || geo?.city || '',
                        state_name: geo?.state?.name || '',
                        postal_code_id: Number(geo?.codigo_postal_id) || undefined,
                    });
                } catch (e) {
                    message.warning('Código postal no encontrado');
                    form.setFieldsValue({ city_name: '', state_name: '', postal_code_id: undefined });
                } finally {
                    setLoadingCP(false);
                }
            } else {
                // Reset de ciudad/estado cuando el CP es incompleto
                form.setFieldsValue({ city_name: '', state_name: '', postal_code_id: undefined });
            }
        }
    };

    const onFinish = async (values) => {
        const payload = {
            ...values,
            second_last_name: values.second_last_name?.trim() || undefined,
            mobile_phone: values.mobile_phone ? String(values.mobile_phone).replace(/\D/g, '') : undefined,
            birth_date: values.birth_date ? values.birth_date.format('YYYY-MM-DD') : undefined,
            postal_code_id: values.postal_code_id ?? undefined,
            height_cm: values.height_cm ? Number(values.height_cm) : undefined,
            weight_kg: values.weight_kg ? Number(values.weight_kg) : undefined,
            weekly_availability_days: values.weekly_availability_days ? Number(values.weekly_availability_days) : undefined,
            gender_id: values.gender_id ? Number(values.gender_id) : undefined,
            main_goal_id: values.main_goal_id ? Number(values.main_goal_id) : undefined,
            fitness_level_id: values.fitness_level_id ? Number(values.fitness_level_id) : undefined,
            nickname: values.nickname?.trim() || undefined,
        };


        try {
            setLoading(true);
            let photoUrlToSend;
            if (mediaFile) {
                const uploadResult = await uploadClientPhoto(mediaFile);
                photoUrlToSend = uploadResult.url ?? uploadResult;
            } else if (mediaRemoved) {
                photoUrlToSend = null;
            } else if (isEdit && originalMediaPath) {
                photoUrlToSend = originalMediaPath;
            }

            if (photoUrlToSend !== undefined) {
                payload.photo_url = photoUrlToSend;
            }
            if (isEdit) await updateClient(id, payload); else await createClient(payload);
            message.success('Cliente guardado correctamente');
            navigate('/clients/all');
        } catch (e) {
            console.error(e);
            message.error('No se pudo guardar el cliente');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div style={{ padding: 24 }}>
            <Card title={isEdit ? 'Editar cliente' : 'Agregar cliente'}>
                <Form form={form} layout="vertical" onValuesChange={onValuesChange} onFinish={onFinish}>
                    <Tabs
                        activeKey={activeTab}
                        onChange={setActiveTab}
                        destroyInactiveTabPane={false}
                        items={[
                            {
                                key: 'client',
                                label: 'Cliente',
                                children: (
                                    <Row gutter={16}>
                                        {/* Datos personales */}
                                        <Col xs={24} md={8}>
                                            <Form.Item name="first_name" label="Nombre" rules={[{ required: true, message: 'Requerido' }]}>
                                                <Input placeholder="Nombre" />
                                            </Form.Item>
                                        </Col>
                                        <Col xs={24} md={16}>
                                            <Form.Item name="last_name" label="Apellidos" rules={[{ required: true, message: 'Requerido' }]}>
                                                <Input placeholder="Apellido" />
                                            </Form.Item>
                                        </Col>
                                        {/* <Col xs={24} md={8}>
                                            <Form.Item name="second_last_name" label="Apellido Materno">
                                                <Input placeholder="Apellido" />
                                            </Form.Item>
                                        </Col> */}


                                        <Col xs={24} md={8}>
                                            <Form.Item name="email" label="Correo electrónico" rules={[{ required: true, message: 'Requerido' }, { type: 'email', message: 'Correo inválido' }]}>
                                                <Input placeholder="correo@email.com" />
                                            </Form.Item>
                                        </Col>
                                        <Col xs={24} md={6}>
                                            <Form.Item
                                                name="mobile_phone"
                                                label="Teléfono móvil"
                                                rules={[{ validator: (_, v) => !v || phone10.test(String(v).replace(/\D/g, '')) ? Promise.resolve() : Promise.reject(new Error('10 dígitos')) }]}
                                                onChange={(e) => {
                                                    const onlyDigits = e.target.value.replace(/\D/g, '');
                                                    form.setFieldsValue({ mobile_phone: onlyDigits });
                                                }}
                                            >
                                                <Input maxLength={10} placeholder="0000000000" />
                                            </Form.Item>
                                        </Col>


                                        <Col xs={24} md={5}>
                                            <Form.Item name="birth_date" label="Fecha de nacimiento" >
                                                <DatePicker
                                                    format="DD/MM/YYYY"
                                                    style={{ width: '100%' }}
                                                    disabledDate={(d) => d && d > dayjs().endOf('day')}
                                                />
                                            </Form.Item>
                                        </Col>
                                        <Col xs={24} md={5}>
                                            <Form.Item name="gender_id" label="Género">
                                                <Select placeholder="..." options={genders.map(g => ({ value: g.id, label: g.name }))} />
                                            </Form.Item>
                                        </Col>

                                        {/* Dirección / CP → Ciudad/Estado */}
                                        <Col xs={24} md={8}>
                                            <Form.Item name="postal_code" label="Código Postal" rules={[{ validator: (_, v) => !v || /^\d{5}$/.test(String(v)) ? Promise.resolve() : Promise.reject(new Error('5 dígitos')) }]}>
                                                <Input placeholder="00000" maxLength={5} />
                                            </Form.Item>
                                        </Col>
                                        <Col xs={24} md={8}>
                                            <Form.Item name="city_name" label="Ciudad">
                                                <Input placeholder="..." disabled />
                                            </Form.Item>
                                        </Col>
                                        <Col xs={24} md={8}>
                                            <Form.Item name="state_name" label="Estado">
                                                <Input placeholder="..." disabled />
                                            </Form.Item>
                                        </Col>
                                        <Form.Item name="postal_code_id" hidden><Input /></Form.Item>

                                        <Col xs={24} md={24}>
                                            <Divider orientation="left">Perfil Fitness</Divider>
                                        </Col>
                                        <Col xs={24} md={12}>
                                            <Form.Item name="main_goal_id" label="Objetivo Principal">
                                                <Select placeholder="..." options={goals.map(x => ({ value: x.id, label: x.name }))} />
                                            </Form.Item>
                                        </Col>
                                        <Col xs={24} md={12}>
                                            <Form.Item name="fitness_level_id" label="Nivel de condición física">
                                                <Select placeholder="..." options={levels.map(x => ({ value: x.id, label: x.name }))} />
                                            </Form.Item>
                                        </Col>


                                        <Col xs={24} md={6}><Form.Item name="height_cm" label="Altura (cm)"><Input /></Form.Item></Col>
                                        <Col xs={24} md={6}><Form.Item name="weight_kg" label="Peso (kg)"><Input /></Form.Item></Col>
                                        <Col xs={24} md={6}>
                                            <Form.Item name="weekly_availability_days" label="Disponibilidad semanal">
                                                <Select placeholder="0 días" options={[0, 1, 2, 3, 4, 5, 6, 7].map(v => ({ value: v, label: `${v} día${v === 1 ? '' : 's'}` }))} />
                                            </Form.Item>
                                        </Col>
                                        <Col xs={24} md={6}>
                                            <Form.Item label="Equipo o Gimnasio" name="has_gym_equipment">
                                                <Radio.Group>
                                                    <Radio value={true}>Sí</Radio>
                                                    <Radio value={false}>No</Radio>
                                                </Radio.Group>
                                            </Form.Item>
                                        </Col>

                                        {/* Consentimientos */}
                                        <Col xs={24} md={24}>
                                            <Divider orientation="left">Consentimiento y Privacidad</Divider>
                                        </Col>
                                        <Col xs={24} md={24}>
                                            <Space direction="vertical">
                                                <Form.Item name="accept_terms" valuePropName="checked" rules={[{ validator: (_, v) => v ? Promise.resolve() : Promise.reject(new Error('Debes aceptar términos')) }]}>
                                                    <Checkbox>Acepto los términos y condiciones</Checkbox>
                                                </Form.Item>
                                                <Form.Item name="allow_marketing" valuePropName="checked">
                                                    <Checkbox> Acepto recibir información y promociones </Checkbox>
                                                </Form.Item>
                                                <Form.Item name="allow_analytics" valuePropName="checked">
                                                    <Checkbox> Autorizo el uso de mis datos para fines estadísticos anónimos </Checkbox>
                                                </Form.Item>
                                            </Space>
                                        </Col>
                                    </Row>
                                ),
                            },
                            {
                                key: 'social',
                                label: 'Social',
                                children: (
                                    <Row gutter={24}>
                                        <Col xs={24} md={12}>
                                            <MediaUpload
                                                allowVideo={false}
                                                maxSizeImage={15}
                                                label="Foto de perfil"
                                                originalMediaPath={originalMediaPath}
                                                isEdit={isEdit}
                                                onChange={handleMediaChange}
                                            />
                                        </Col>
                                        <Col xs={24} md={12}>
                                            <Form.Item
                                                name="nickname"
                                                label="Nickname"
                                                validateTrigger="onBlur"
                                                rules={[{ validator: nicknameValidator }]}
                                                hasFeedback
                                                validateStatus={nicknameValidateStatus}
                                                extra={nicknameAvailability === 'available' ? 'Este nickname está disponible.' : undefined}
                                            >
                                                <Input placeholder="Ej. sthenus_master" maxLength={60} />
                                            </Form.Item>
                                        </Col>
                                    </Row>
                                ),
                            },
                            {
                                key: 'history',
                                label: 'Historial',
                                children: <ClientSubscriptionsHistory clientId={id} />,
                            },
                        ]}
                    />

                    <Row justify="end" gutter={12} style={{ marginTop: 24 }}>
                        <Col>
                            <Button onClick={() => navigate('/clients/all')} disabled={loading}>
                                Cancelar
                            </Button>
                        </Col>
                        <Col>
                            <Button type="primary" htmlType="submit" loading={loading || loadingCP}>
                                {isEdit ? 'Guardar cambios' : 'Guardar'}
                            </Button>
                        </Col>
                    </Row>
                </Form>
            </Card>
        </div>
    );
}